import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRivalry } from '../contexts/RivalryContext';
import { RivalryLeaderboard } from '../components/rivalry/RivalryLeaderboard';
import { RivalryChat } from '../components/rivalry/RivalryChat';
import supabase from '../supabaseClient';

/**
 * Main rivalry dashboard - shows leaderboard, chat, and stats
 */
export function RivalryDashboard() {
  const { rivalryId } = useParams();
  const navigate = useNavigate();
  const {
    activeRivalry,
    selectRivalry,
    loading,
    leaderboard,
    messages,
    userStats,
    currentSeason,
    sendMessage,
  } = useRivalry();

  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'leaderboard', 'chat'

  useEffect(() => {
    // Get current user ID
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) {
        setCurrentUserId(data.user.id);
      }
    });

    // Load rivalry
    if (rivalryId) {
      selectRivalry(rivalryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rivalryId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading rivalry...</div>
      </div>
    );
  }

  if (!activeRivalry) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Rivalry not found</p>
          <button onClick={() => navigate('/rivalries')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">
            Back to Rivalries
          </button>
        </div>
      </div>
    );
  }

  const handleSendMessage = async (content) => {
    const result = await sendMessage(content);
    return result;
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button onClick={() => navigate('/rivalries')} className="text-gray-400 hover:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Rivalries
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">{activeRivalry.name}</h1>
              {activeRivalry.description && <p className="text-gray-400 mb-2">{activeRivalry.description}</p>}
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>{activeRivalry.current_participants} / {activeRivalry.max_participants} participants</span>
                {currentSeason && (
                  <span>Season {currentSeason.season_number}</span>
                )}
              </div>
            </div>

            <button className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors">
              Invite Friends
            </button>
          </div>
        </div>

        {/* User Stats Banner */}
        {userStats && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-blue-100 text-sm mb-1">Your Rank</p>
                <p className="text-3xl font-bold text-white">#{userStats.current_rank || '-'}</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Points This Month</p>
                <p className="text-3xl font-bold text-white">{userStats.monthly_points}</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Workouts</p>
                <p className="text-3xl font-bold text-white">{userStats.workouts_completed}</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Current Streak</p>
                <p className="text-3xl font-bold text-white flex items-center justify-center">
                  {userStats.current_streak}
                  {userStats.current_streak > 0 && (
                    <svg className="w-6 h-6 ml-2 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                    </svg>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-700">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'overview'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'leaderboard'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Leaderboard
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'chat'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Chat
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Leaderboard Preview */}
            <div className="lg:col-span-2">
              <RivalryLeaderboard leaderboard={leaderboard} currentUserId={currentUserId} />
            </div>

            {/* Chat Preview */}
            <div className="lg:col-span-1">
              <RivalryChat
                messages={messages}
                onSendMessage={handleSendMessage}
                currentUserId={currentUserId}
                className="h-[600px]"
              />
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="max-w-4xl mx-auto">
            <RivalryLeaderboard leaderboard={leaderboard} currentUserId={currentUserId} />
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="max-w-4xl mx-auto">
            <RivalryChat
              messages={messages}
              onSendMessage={handleSendMessage}
              currentUserId={currentUserId}
              className="h-[700px]"
            />
          </div>
        )}

        {/* Season Info */}
        {currentSeason && (
          <div className="mt-8 bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Current Season</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Season Number</p>
                <p className="text-white font-bold text-2xl">{currentSeason.season_number}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Ends</p>
                <p className="text-white font-semibold">{new Date(currentSeason.end_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Workouts</p>
                <p className="text-white font-bold text-2xl">{currentSeason.total_workouts}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
